import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/auth/AuthProvider";
import ImageUpload from "@/components/scan/ImageUpload";

interface NewProductFormProps {
  detectedText?: string;
  scanId?: string;
  imageFile?: File;
  productInfo?: {
    brand?: string;
    productName?: string;
    volume?: string;
    ingredients?: string[];
  };
}

const NewProductForm = ({ detectedText, scanId, imageFile: initialImageFile, productInfo }: NewProductFormProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [imageFile, setImageFile] = useState<File | undefined>(initialImageFile);
  const [formData, setFormData] = useState({
    name: "",
    brand: "",
    description: "",
    price: "",
    rating: "5",
    review: "",
    purchaseLocation: ""
  });

  useEffect(() => {
    // Pre-fill form with extracted information if available
    const extractedInfo = location.state?.extractedInfo;
    if (extractedInfo) {
      console.log('Pre-filling form with extracted info:', extractedInfo);
      setFormData(prev => ({
        ...prev,
        name: extractedInfo.name || prev.name,
        brand: extractedInfo.brand || prev.brand,
        description: extractedInfo.description || prev.description
      }));
    }

    // Pre-fill from detected product info if available
    if (productInfo) {
      console.log('Pre-filling form with detected product info:', productInfo);
      setFormData(prev => ({
        ...prev,
        name: productInfo.productName || prev.name,
        brand: productInfo.brand || prev.brand,
      }));
    }
  }, [location.state, productInfo]);

  const handleImageSelect = (file: File) => {
    console.log('Image selected:', {
      name: file.name,
      size: file.size,
      type: file.type
    });
    setImageFile(file);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      toast({
        title: t("auth.required"),
        description: t("auth.pleaseLoginFirst"),
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);
    try {
      let imageUrl = null;

      // Upload image if provided
      if (imageFile) {
        console.log('Uploading product image:', {
          name: imageFile.name,
          size: imageFile.size,
          type: imageFile.type
        });

        const fileExt = imageFile.name.split('.').pop();
        const filePath = `${crypto.randomUUID()}.${fileExt}`;

        const { error: uploadError, data } = await supabase.storage
          .from('products')
          .upload(filePath, imageFile);

        if (uploadError) {
          throw uploadError;
        }

        const { data: { publicUrl } } = supabase.storage
          .from('products')
          .getPublicUrl(filePath);

        imageUrl = publicUrl;
      }

      // Create product
      const { data: product, error: productError } = await supabase
        .from('products')
        .insert({
          name: formData.name,
          brand: formData.brand,
          description: formData.description,
          price: parseFloat(formData.price),
          user_id: user.id,
          image_url: imageUrl,
          detected_text: detectedText,
          rating: parseFloat(formData.rating)
        })
        .select()
        .single();

      if (productError) throw productError;

      // Create review if rating or comment provided
      if (formData.rating || formData.review) {
        const { error: reviewError } = await supabase
          .from('reviews')
          .insert({
            product_id: product.id,
            user_id: user.id,
            rating: parseInt(formData.rating),
            comment: formData.review,
            purchase_location: formData.purchaseLocation
          });

        if (reviewError) {
          console.error('Error creating review:', reviewError);
          throw reviewError;
        }
      }

      // Update scan with product id if scanId exists
      if (scanId && product) {
        const { error: scanError } = await supabase
          .from('scans')
          .update({ product_id: product.id })
          .eq('id', scanId);

        if (scanError) {
          console.error('Error updating scan:', scanError);
        }
      }

      toast({
        title: t("products.productCreated"),
        description: t("products.productCreatedSuccess"),
      });

      navigate(`/product/${product.id}`);
    } catch (error) {
      console.error('Error creating product:', error);
      toast({
        title: t("errors.error"),
        description: t("errors.productCreationFailed"),
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {!imageFile && (
        <div className="mb-4">
          <ImageUpload onImageSelect={handleImageSelect} />
        </div>
      )}

      {imageFile && (
        <div className="mb-4">
          <img
            src={URL.createObjectURL(imageFile)}
            alt="Product preview"
            className="w-full max-w-md mx-auto rounded-lg shadow-md object-cover aspect-square"
          />
          <Button 
            type="button" 
            variant="outline" 
            className="mt-2"
            onClick={() => setImageFile(undefined)}
          >
            {t("common.change")}
          </Button>
        </div>
      )}

      {detectedText && (
        <div className="p-4 bg-muted rounded-lg mb-4">
          <p className="text-sm font-medium">{t("products.detectedText")}:</p>
          <p className="text-sm text-muted-foreground">{detectedText}</p>
        </div>
      )}

      {productInfo && (Object.values(productInfo).some(v => v && (typeof v === 'string' ? v.length > 0 : v.length > 0))) && (
        <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg mb-4">
          <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
            {t("products.detectedInfo")}:
          </p>
          <div className="mt-2 space-y-1">
            {productInfo.brand && (
              <p className="text-sm"><span className="font-medium">{t("products.brand")}:</span> {productInfo.brand}</p>
            )}
            {productInfo.productName && (
              <p className="text-sm"><span className="font-medium">{t("products.name")}:</span> {productInfo.productName}</p>
            )}
            {productInfo.volume && (
              <p className="text-sm"><span className="font-medium">{t("products.volume")}:</span> {productInfo.volume}</p>
            )}
          </div>
        </div>
      )}

      <div>
        <label htmlFor="name" className="block text-sm font-medium mb-1">
          {t("products.name")}
        </label>
        <Input
          id="name"
          name="name"
          value={formData.name}
          onChange={handleInputChange}
          required
        />
      </div>

      <div>
        <label htmlFor="brand" className="block text-sm font-medium mb-1">
          {t("products.brand")}
        </label>
        <Input
          id="brand"
          name="brand"
          value={formData.brand}
          onChange={handleInputChange}
          required
        />
      </div>

      <div>
        <label htmlFor="description" className="block text-sm font-medium mb-1">
          {t("products.description")}
        </label>
        <Textarea
          id="description"
          name="description"
          value={formData.description}
          onChange={handleInputChange}
          required
        />
      </div>

      <div>
        <label htmlFor="price" className="block text-sm font-medium mb-1">
          {t("products.price")}
        </label>
        <Input
          id="price"
          name="price"
          type="number"
          step="0.01"
          min="0"
          value={formData.price}
          onChange={handleInputChange}
          required
        />
      </div>

      <div>
        <label htmlFor="rating" className="block text-sm font-medium mb-1">
          {t("products.rating")} (1-5)
        </label>
        <Input
          id="rating"
          name="rating"
          type="number"
          min="1"
          max="5"
          value={formData.rating}
          onChange={handleInputChange}
          required
        />
      </div>

      <div>
        <label htmlFor="review" className="block text-sm font-medium mb-1">
          {t("products.review")}
        </label>
        <Textarea
          id="review"
          name="review"
          value={formData.review}
          onChange={handleInputChange}
          placeholder={t("products.shareExperience")}
        />
      </div>

      <div>
        <label htmlFor="purchaseLocation" className="block text-sm font-medium mb-1">
          {t("products.purchaseLocation")}
        </label>
        <Input
          id="purchaseLocation"
          name="purchaseLocation"
          value={formData.purchaseLocation}
          onChange={handleInputChange}
          placeholder={t("products.whereBought")}
        />
      </div>

      <Button type="submit" className="w-full" disabled={isLoading}>
        {isLoading ? t("common.saving") : t("products.createProduct")}
      </Button>
    </form>
  );
};

export default NewProductForm;
